// Blueprint record forms — one declarative spec per map entity, one small
// form component that renders it. Everything is a registry entity
// (bizSystem, dataStore, bizProcess, orgUnit, orgRole), so saving is
// createEntity / updateEntity; the shapes here mirror registry.js.
import React, { useEffect, useState } from 'react';
import { LEVERS } from '../../data/blueprintForecast';

export const SYSTEM_CATEGORIES = ['erp', 'crm', 'hris', 'ticketing', 'email', 'bi', 'spreadsheet', 'custom', 'other'];
export const VERDICTS = ['undecided', 'keep', 'augment', 'replace', 'retire'];
export const DATA_KINDS = ['database', 'warehouse', 'lake', 'files', 'saasApi', 'spreadsheet', 'other'];
export const SENSITIVITIES = ['public', 'internal', 'confidential', 'regulated'];
export const LEVER_IDS = LEVERS.map((l) => l.id);

export const usd = (v) => (Number.isFinite(Number(v)) && Number(v) !== 0 ? `$${Math.round(Number(v)).toLocaleString()}` : '—');
export const uid = () => Math.random().toString(36).slice(2, 9);

// kind → [{ key, label, type: text|number|select|textarea|list|ref|check, options?, ref?, help? }]
export const SPECS = {
  bizSystem: {
    label: 'System', plural: 'Systems', entity: 'bizSystem', accent: '#60a5fa',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'category', label: 'Category', type: 'select', options: SYSTEM_CATEGORIES },
      { key: 'vendor', label: 'Vendor', type: 'text' },
      { key: 'annualCostUsd', label: 'Yearly cost (USD)', type: 'number' },
      { key: 'userCount', label: 'People who use it', type: 'number' },
      { key: 'ownerRoleId', label: 'Owned by role', type: 'ref', ref: 'orgRole' },
      { key: 'integrationId', label: 'Connected integration', type: 'ref', ref: 'integration' },
      { key: 'verdict', label: 'Verdict', type: 'select', options: VERDICTS },
      { key: 'painPoints', label: 'What hurts (one per line)', type: 'list' },
      { key: 'description', label: 'Notes', type: 'textarea' },
    ],
  },
  dataStore: {
    label: 'Data store', plural: 'Data', entity: 'dataStore', accent: '#2dd4bf',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'kind', label: 'Kind', type: 'select', options: DATA_KINDS },
      { key: 'engine', label: 'Engine / product', type: 'text', help: 'Postgres, Snowflake, S3, SharePoint, Excel…' },
      { key: 'sizeGb', label: 'Size (GB)', type: 'number' },
      { key: 'sensitivity', label: 'Sensitivity', type: 'select', options: SENSITIVITIES },
      { key: 'systemIds', label: 'Used by systems', type: 'refs', ref: 'bizSystem' },
      { key: 'datasetId', label: 'Training Center dataset', type: 'ref', ref: 'dataset' },
      { key: 'description', label: 'Notes', type: 'textarea' },
    ],
  },
  bizProcess: {
    label: 'Process', plural: 'Processes', entity: 'bizProcess', accent: '#f59e0b',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'ownerUnitId', label: 'Owned by unit', type: 'ref', ref: 'orgUnit' },
      { key: 'volumePerMonth', label: 'Times per month', type: 'number' },
      { key: 'cycleHours', label: 'Hours start to finish', type: 'number' },
      { key: 'steps', label: 'Steps', type: 'steps' },
      { key: 'painPoints', label: 'What hurts (one per line)', type: 'list' },
      { key: 'description', label: 'Notes', type: 'textarea' },
    ],
  },
  orgUnit: {
    label: 'Unit', plural: 'Units', entity: 'orgUnit', accent: '#f472b6',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'parentId', label: 'Part of', type: 'ref', ref: 'orgUnit' },
      { key: 'headRoleId', label: 'Led by role', type: 'ref', ref: 'orgRole' },
      { key: 'headcount', label: 'Headcount', type: 'number' },
    ],
  },
  orgRole: {
    label: 'Role', plural: 'Roles', entity: 'orgRole', accent: '#a78bfa',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'unitId', label: 'In unit', type: 'ref', ref: 'orgUnit' },
      { key: 'headcount', label: 'Headcount', type: 'number' },
      { key: 'loadedAnnualCostUsd', label: 'Loaded yearly cost per head (USD)', type: 'number', help: 'Salary + benefits + overhead. Prices minutes saved.' },
      { key: 'processIds', label: 'Works these processes', type: 'refs', ref: 'bizProcess' },
      { key: 'systemIds', label: 'Uses these systems', type: 'refs', ref: 'bizSystem' },
      { key: 'augmentable', label: 'An agent could sit beside this role', type: 'check' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
};

const blank = (spec) => Object.fromEntries(spec.fields.map((f) => [f.key, f.type === 'number' ? 0 : f.type === 'list' || f.type === 'refs' || f.type === 'steps' ? [] : f.type === 'check' ? true : f.type === 'ref' ? null : '']));

export function RecordForm({ spec, record, refs = {}, canEdit, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState(() => ({ ...blank(spec), ...(record || {}) }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setDraft({ ...blank(spec), ...(record || {}) }); }, [spec, record]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    if (!draft.name?.trim()) { setErr('A name is needed.'); return; }
    setBusy(true); setErr('');
    try {
      const out = {};
      for (const f of spec.fields) {
        let v = draft[f.key];
        if (f.type === 'number') v = Number(v) || 0;
        if (f.type === 'ref' && !v) v = null;
        if (f.type === 'text' || f.type === 'textarea') v = String(v || '').trim();
        out[f.key] = v;
      }
      await onSave(out);
    } catch (x) { setErr(x.message || String(x)); }
    setBusy(false);
  };
  return (
    <form className="bp-form" onSubmit={save} data-nova-note={`${record?.id ? 'Editing' : 'New'} ${spec.label.toLowerCase()} form`}>
      {spec.fields.map((f) => (
        <label key={f.key} className={`bp-field${f.type === 'textarea' || f.type === 'steps' || f.type === 'list' || f.type === 'refs' ? ' bp-wide' : ''}`}>
          <span>{f.label}{f.required ? ' *' : ''}</span>
          <Field f={f} value={draft[f.key]} refs={refs} disabled={!canEdit} onChange={(v) => set(f.key, v)} />
          {f.help && <small className="cs-help">{f.help}</small>}
        </label>
      ))}
      {err && <p className="cs-help" style={{ color: 'var(--danger)' }} role="alert">{err}</p>}
      <div className="bp-form-actions">
        {canEdit && <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy}>{busy ? 'Saving…' : record?.id ? 'Save' : `Add ${spec.label.toLowerCase()}`}</button>}
        {onCancel && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={onCancel}>Cancel</button>}
        {record?.id && onDelete && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginLeft: 'auto', color: 'var(--danger)' }} data-agent-guard="true" onClick={() => { if (window.confirm(`Remove ${record.name}? Initiatives that target it keep the id but lose the name.`)) onDelete(); }}>Remove</button>}
      </div>
    </form>
  );
}

function Field({ f, value, refs, disabled, onChange }) {
  const options = (name) => (refs[name] || []).map((r) => ({ id: r.id, label: r.name || r.title || r.id }));
  switch (f.type) {
    case 'number': return <input className="cs-input" type="number" step="any" value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'select': return <select className="cs-input" value={value || f.options[0]} disabled={disabled} onChange={(e) => onChange(e.target.value)}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
    case 'textarea': return <textarea className="cs-input" rows={3} value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'list': return <textarea className="cs-input" rows={3} value={(value || []).join('\n')} disabled={disabled} onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />;
    case 'check': return <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
    case 'ref': return (
      <select className="cs-input" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>
        {options(f.ref).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    );
    case 'refs': return (
      <div className="bp-checks">
        {options(f.ref).length === 0 && <span className="cs-help">none mapped yet</span>}
        {options(f.ref).map((o) => (
          <label key={o.id}><input type="checkbox" disabled={disabled} checked={(value || []).includes(o.id)} onChange={(e) => onChange(e.target.checked ? [...(value || []), o.id] : (value || []).filter((x) => x !== o.id))} /> {o.label}</label>
        ))}
      </div>
    );
    case 'steps': return <StepsEditor steps={value || []} refs={refs} disabled={disabled} onChange={onChange} />;
    default: return <input className="cs-input" type="text" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  }
}

export function StepsEditor({ steps, refs, disabled, onChange }) {
  const roles = refs.orgRole || []; const systems = refs.bizSystem || [];
  const upd = (i, patch) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const totalMin = steps.reduce((s, x) => s + (Number(x.minutes) || 0), 0);
  const manualMin = steps.filter((x) => x.manual !== false).reduce((s, x) => s + (Number(x.minutes) || 0), 0);
  return (
    <div className="bp-steps">
      {steps.map((s, i) => (
        <div key={s.id || i} className="bp-step">
          <span className="bp-step-n">{i + 1}</span>
          <input className="cs-input" placeholder="What happens" value={s.text || ''} disabled={disabled} onChange={(e) => upd(i, { text: e.target.value })} />
          <select className="cs-input" value={s.roleId || ''} disabled={disabled} onChange={(e) => upd(i, { roleId: e.target.value || null })} aria-label="role"><option value="">role…</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
          <select className="cs-input" value={s.systemId || ''} disabled={disabled} onChange={(e) => upd(i, { systemId: e.target.value || null })} aria-label="system"><option value="">system…</option>{systems.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
          <input className="cs-input" type="number" min="0" step="any" style={{ width: 70 }} value={s.minutes ?? ''} disabled={disabled} onChange={(e) => upd(i, { minutes: Number(e.target.value) || 0 })} aria-label="minutes" title="minutes per occurrence" />
          <label className="cs-help" title="A person does it by hand"><input type="checkbox" checked={s.manual !== false} disabled={disabled} onChange={(e) => upd(i, { manual: e.target.checked })} /> manual</label>
          {!disabled && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onChange(steps.filter((_, j) => j !== i))} aria-label="remove step">×</button>}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {!disabled && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onChange([...steps, { id: uid(), text: '', roleId: null, systemId: null, minutes: 5, manual: true }])}>+ Step</button>}
        <span className="cs-help">{steps.length} steps · {totalMin} min each time · {manualMin} min by hand</span>
      </div>
    </div>
  );
}
